import { useState, useRef, useEffect, useCallback, memo } from "react";
import "./OmAssist.css";

// ─── Knowledge Base ───────────────────────────────────────────────────────────
interface QAEntry {
  patterns: string[];
  response: string;
}

const KB: QAEntry[] = [
  {
    patterns: ["hi", "hello", "hey", "hii", "helo", "sup", "good morning", "good evening", "howdy"],
    response:
      "👋 Hey there! I'm **Om Assist**, your dedicated SmartShrink guide!\n\nI can help you with:\n• 🚀 Project features & modules\n• 👨‍💻 Developer information\n• 📋 How to use SmartShrink\n• 🔧 Technical details\n\nWhat would you like to know?",
  },
  {
    patterns: ["who are you", "what are you", "introduce yourself", "about you", "your name", "who is this"],
    response:
      "🤖 I'm **Om Assist** — an AI assistant built specifically for the **SmartShrink** platform!\n\nI'm here 24/7 to help you explore all the powerful features SmartShrink offers. From file compression to AI-powered career coaching — I know it all!\n\nAsk me anything about the platform or the developer! 😊",
  },
  {
    patterns: ["developer", "who made", "who built", "who created", "who developed", "creator", "author", "who is om", "om prakash", "lenka"],
    response:
      "👨‍💻 **Om Prakash Lenka** — Developer of SmartShrink\n\n━━━━━━━━━━━━━━━━━━━━━━━\n🏢 **Role:** Software Engineer\n🏦 **Company:** TCS (Tata Consultancy Services)\n💡 **Expertise:** Full-Stack Development, AI Integration, Cloud Architecture\n🎯 **Vision:** Building intelligent tools that make everyday digital tasks effortless\n\nOm Prakash designed SmartShrink with a focus on performance, premium UX, and AI-powered intelligence — all in one platform!",
  },
  {
    patterns: ["tcs", "tata consultancy", "company", "where does he work", "where does developer work"],
    response:
      "🏢 Om Prakash Lenka works at **TCS (Tata Consultancy Services)** — one of the world's largest IT services companies.\n\nAs a **Software Engineer at TCS**, he brings enterprise-grade engineering practices to SmartShrink, resulting in a highly reliable, scalable, and polished platform!",
  },
  {
    patterns: ["what is smartshrink", "about project", "about this app", "tell me about", "what does this do", "project overview", "overview", "what can it do"],
    response:
      "🚀 **SmartShrink** — Your All-in-One Intelligent Platform\n\n━━━━━━━━━━━━━━━━━━━━━━━\nSmartShrink is a premium AI-powered productivity platform that combines:\n\n🖼️ **Image Optimization** — Compress images without quality loss\n📄 **PDF Compression** — Shrink PDFs intelligently\n🧠 **Vision Studio** — AI document analysis & extraction\n📚 **EduStudio** — AI-powered education assistant\n💼 **Career Sync** — AI Mock Interviewer & career coach\n🏗️ **Architect Studio** — Architecture & diagram intelligence\n\nAll modules are unified under a beautiful, dark-themed premium interface!",
  },
  {
    patterns: ["image", "image compress", "image optimizer", "compress image", "photo", "jpg", "png", "webp", "image uploader"],
    response:
      "🖼️ **Image Optimizer** — Smart Compression Engine\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ Compress JPG, PNG, WebP & more\n✅ Preserve visual quality while drastically reducing file size\n✅ Drag & Drop interface\n✅ Instant side-by-side comparison\n✅ Batch processing support\n✅ Download optimized images instantly\n\n📍 Available at the **Home Page** — just drag and drop your image to get started!",
  },
  {
    patterns: ["pdf", "pdf compress", "compress pdf", "pdf uploader", "pdf shrink", "reduce pdf"],
    response:
      "📄 **PDF Compressor** — Intelligent PDF Shrinking\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ Compress large PDF files efficiently\n✅ Maintain document quality & formatting\n✅ Fast processing powered by backend optimization\n✅ Download compressed PDF instantly\n\n📍 Navigate to **PDF Shrink** from the top navigation menu to use this feature!",
  },
  {
    patterns: ["vision studio", "document studio", "document analysis", "ocr", "extract text", "document ai", "vision"],
    response:
      "🧠 **Vision Studio** — AI Document Intelligence\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ AI-powered document analysis & understanding\n✅ Extract key information from images & PDFs\n✅ Smart text recognition (OCR capabilities)\n✅ Document summarization & insights\n✅ Powered by advanced vision AI models\n\n📍 Access via **Vision Studio** in the Intelligence dropdown menu!",
  },
  {
    patterns: ["edu studio", "education", "study", "learning", "student", "notes", "quiz", "educational"],
    response:
      "📚 **EduStudio** — AI-Powered Learning Assistant\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ Upload study materials & get instant summaries\n✅ AI-generated quizzes from your documents\n✅ Smart concept extraction\n✅ Ideal for students and professionals\n✅ Accelerate your learning with AI\n\n📍 Find it under **EduStudio** in the Intelligence dropdown!",
  },
  {
    patterns: ["career sync", "career", "interview", "resume", "job", "mock interview", "career coach", "ai interview", "hiring"],
    response:
      "💼 **Career Sync** — AI Mock Interviewer & Career Coach\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ AI-powered mock interviews tailored to your profile\n✅ Resume analysis & scoring with Groq AI\n✅ Real-time feedback & improvement tips\n✅ Industry-specific interview questions\n✅ Performance tracking & reports\n✅ Ideal for job seekers & professionals\n\n📍 Access via **Career Sync** from the top navbar!",
  },
  {
    patterns: ["architect studio", "architecture", "diagram", "design", "system design", "architect"],
    response:
      "🏗️ **Architect Studio** — Architecture Intelligence\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ AI-powered architecture diagram analysis\n✅ System design assistance\n✅ Intelligent architecture insights\n✅ Perfect for developers & architects\n\n📍 Available under **Architect Studio** in the Intelligence menu!",
  },
  {
    patterns: ["general uploader", "uploader", "upload file", "file upload"],
    response:
      "📁 **General Uploader** — Universal File Processing\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ Upload any supported file type\n✅ Intelligent processing based on file format\n✅ Flexible and versatile file handler\n\n📍 Access via the **Uploader** route on the platform!",
  },
  {
    patterns: ["features", "modules", "what features", "all features", "capabilities", "tools", "list features"],
    response:
      "✨ **SmartShrink Feature Suite**\n\n━━━━━━━━━━━━━━━━━━━━━━━\n1️⃣ 🖼️ **Image Optimizer** — Compress images\n2️⃣ 📄 **PDF Compressor** — Shrink PDFs\n3️⃣ 🧠 **Vision Studio** — AI document analysis\n4️⃣ 📚 **EduStudio** — AI learning assistant\n5️⃣ 💼 **Career Sync** — AI mock interviewer\n6️⃣ 🏗️ **Architect Studio** — Architecture AI\n7️⃣ 📁 **General Uploader** — File processing\n\nAll features are accessible from the top navigation bar. Which one would you like to know more about?",
  },
  {
    patterns: ["tech stack", "technology", "built with", "framework", "react", "backend", "frontend", "how is it built"],
    response:
      "⚙️ **SmartShrink Tech Stack**\n\n━━━━━━━━━━━━━━━━━━━━━━━\n**Frontend:**\n• ⚛️ React + TypeScript\n• 🎨 Custom CSS with Glassmorphism\n• 📦 Bootstrap 5\n• 🔀 React Router DOM\n\n**Backend:**\n• 🟢 Node.js + Express\n• 🗄️ Database integration\n• 🤖 Groq AI API\n• 🧠 Google Gemini AI\n\n**Design System:**\n• Premium dark theme\n• Outfit + Inter fonts\n• Micro-animations & mesh gradients",
  },
  {
    patterns: ["login", "signin", "sign in", "account", "register", "signup", "sign up", "auth"],
    response:
      "🔐 **Authentication on SmartShrink**\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✅ Secure login with email & password\n✅ New user registration / sign-up\n✅ JWT-based authentication\n✅ Session persistence\n\n📍 Click **Sign In** or **Get Started** from the top navbar to access your account!",
  },
  {
    patterns: ["how to use", "get started", "how do i", "help", "guide", "tutorial", "instructions", "steps"],
    response:
      "🚀 **Getting Started with SmartShrink**\n\n━━━━━━━━━━━━━━━━━━━━━━━\n**Step 1:** 📝 Create a free account (Sign Up)\n**Step 2:** 🔑 Log in to your account\n**Step 3:** 🧭 Choose a tool from the Navbar\n**Step 4:** 📁 Upload your file or start a session\n**Step 5:** ✨ Let AI do the magic!\n\nAll tools are intuitive and work with drag-and-drop. No technical knowledge required! 😊",
  },
  {
    patterns: ["free", "pricing", "cost", "paid", "subscription", "plan"],
    response:
      "💰 **SmartShrink Pricing**\n\nSmartShrink is currently available with core features accessible after creating a free account.\n\n✅ Free account to get started\n✅ Access to compression tools\n✅ AI-powered features available\n\nFor the latest pricing details, check the platform homepage or contact the developer!",
  },
  {
    patterns: ["contact", "reach out", "support", "help me", "contact developer", "email"],
    response:
      "📬 **Contact & Support**\n\n━━━━━━━━━━━━━━━━━━━━━━━\n👨‍💻 **Developer:** Om Prakash Lenka\n🏢 **Organization:** TCS (Tata Consultancy Services)\n\nFor support or inquiries, feel free to reach out through the platform's official channels.\n\nIs there anything specific I can help you with right now? 😊",
  },
  {
    patterns: ["ai", "artificial intelligence", "groq", "gemini", "ai powered", "machine learning", "model"],
    response:
      "🤖 **AI Powers SmartShrink**\n\n━━━━━━━━━━━━━━━━━━━━━━━\nSmartShrink leverages cutting-edge AI:\n\n• 🧠 **Groq AI** — Ultra-fast LLM inference for Career Sync & resume analysis\n• 👁️ **Google Gemini** — Vision & document intelligence for Vision Studio\n• 🔬 **Custom Models** — Optimized compression algorithms\n\nThe AI integration gives SmartShrink its intelligent edge over traditional tools!",
  },
  {
    patterns: ["dark mode", "theme", "design", "ui", "interface", "look", "style", "beautiful"],
    response:
      "🎨 **SmartShrink Design Philosophy**\n\n━━━━━━━━━━━━━━━━━━━━━━━\n✨ **Glassmorphism** — Frosted glass UI elements\n🌌 **Dark Theme** — Premium dark mode throughout\n🎭 **Mesh Gradients** — Dynamic color backgrounds\n⚡ **Micro-animations** — Smooth, fluid interactions\n🔤 **Outfit + Inter** — Premium typography\n\nDesigned by Om Prakash Lenka to feel like a top-tier SaaS product!",
  },
  {
    patterns: ["thank", "thanks", "thank you", "appreciated", "great", "awesome", "nice", "cool", "perfect", "wonderful"],
    response:
      "😊 You're welcome! I'm glad I could help!\n\nDon't hesitate to ask if you have more questions about SmartShrink. I'm always here to assist!\n\n✨ **Om Assist** — Always at your service 🚀",
  },
  {
    patterns: ["bye", "goodbye", "see you", "later", "exit", "close", "quit"],
    response:
      "👋 Goodbye! It was great chatting with you!\n\nCome back anytime — I'm always here to help you get the most out of **SmartShrink**! 🚀\n\n— *Om Assist*",
  },
];

// ─── Helpers ──────────────────────────────────────────────────────────────────
function getResponse(input: string): string {
  const normalized = input.toLowerCase().trim();
  for (const entry of KB) {
    if (entry.patterns.some((p) => normalized.includes(p))) {
      return entry.response;
    }
  }
  return "🤔 Hmm, I'm not sure about that specific question!\n\nHere's what I can help you with:\n• 🚀 SmartShrink features & modules\n• 👨‍💻 Developer info (Om Prakash Lenka)\n• 📋 How to use the platform\n• ⚙️ Tech stack & AI integrations\n\nTry asking about a specific feature like *\"Image Optimizer\"*, *\"Career Sync\"*, or *\"Who is the developer?\"*";
}

function formatMessage(text: string) {
  const parts = text.split(/\*\*(.*?)\*\*/g);
  return (
    <span>
      {parts.map((part, i) =>
        i % 2 === 1 ? <strong key={i}>{part}</strong> : part
      )}
    </span>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────
interface Message {
  id: string;
  role: "user" | "bot";
  text: string;
  timestamp: Date;
}

const QUICK_ACTIONS = [
  { label: "✨ Features", query: "What features does SmartShrink have?" },
  { label: "👨‍💻 Developer", query: "Tell me about the developer" },
  { label: "🚀 Get Started", query: "How do I get started?" },
  { label: "🤖 AI Tech", query: "What AI powers SmartShrink?" },
];

// ─── Component ────────────────────────────────────────────────────────────────
function OmAssist() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "bot",
      text: "👋 Hi! I'm **Om Assist**, your SmartShrink guide!\n\nAsk me anything about the platform features, the developer **Om Prakash Lenka**, or how to get started!",
      timestamp: new Date(),
    },
  ]);
  const [input, setInput] = useState("");
  const [typing, setTyping] = useState(false);
  const [hasNewMsg, setHasNewMsg] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
      setTimeout(() => inputRef.current?.focus(), 100);
      setHasNewMsg(false);
    }
  }, [messages, open]);

  const sendMessage = useCallback(
    (text: string) => {
      const trimmed = text.trim();
      if (!trimmed) return;

      const userMsg: Message = {
        id: Date.now().toString(),
        role: "user",
        text: trimmed,
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, userMsg]);
      setInput("");
      setTyping(true);

      setTimeout(() => {
        const botMsg: Message = {
          id: (Date.now() + 1).toString(),
          role: "bot",
          text: getResponse(trimmed),
          timestamp: new Date(),
        };
        setMessages((prev) => [...prev, botMsg]);
        setTyping(false);
        if (!open) setHasNewMsg(true);
      }, 600 + Math.random() * 400);
    },
    [open]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const formatTime = (d: Date) =>
    d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

  return (
    <>
      {/* ── Toggle Button ─────────────────────────────────────────── */}
      <button
        id="om-assist-toggle"
        className={`oa-toggle ${open ? "oa-toggle--open" : ""}`}
        onClick={() => setOpen((v) => !v)}
        aria-label="Toggle Om Assist chatbot"
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        ) : (
          <>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
              <circle cx="9" cy="10" r="1" fill="currentColor" />
              <circle cx="12" cy="10" r="1" fill="currentColor" />
              <circle cx="15" cy="10" r="1" fill="currentColor" />
            </svg>
            {hasNewMsg && <span className="oa-badge" />}
          </>
        )}
        <div className="oa-toggle__ring" />
      </button>

      {/* ── Chat Window ───────────────────────────────────────────── */}
      <div className={`oa-window ${open ? "oa-window--open" : ""}`} id="om-assist-window" role="dialog" aria-label="Om Assist Chat">
        {/* Header */}
        <div className="oa-header">
          <div className="oa-header__avatar">
            <span>O</span>
            <span className="oa-header__avatar-dot" />
          </div>
          <div className="oa-header__info">
            <p className="oa-header__name">Om Assist</p>
            <p className="oa-header__status">
              <span className="oa-status-dot" />
              SmartShrink AI Guide · Online
            </p>
          </div>
          <div className="oa-header__brand">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ opacity: 0.5 }}>
              <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
          </div>
        </div>

        {/* Messages */}
        <div className="oa-messages custom-scrollbar">
          {messages.map((msg) => (
            <div key={msg.id} className={`oa-msg oa-msg--${msg.role}`}>
              {msg.role === "bot" && (
                <div className="oa-msg__avatar">O</div>
              )}
              <div className="oa-msg__bubble">
                {msg.text.split("\n").map((line, i) => (
                  <p key={i} className={line.startsWith("━") ? "oa-divider" : ""}>
                    {formatMessage(line)}
                  </p>
                ))}
                <span className="oa-msg__time">{formatTime(msg.timestamp)}</span>
              </div>
            </div>
          ))}

          {typing && (
            <div className="oa-msg oa-msg--bot">
              <div className="oa-msg__avatar">O</div>
              <div className="oa-msg__bubble oa-typing">
                <span /><span /><span />
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Quick Actions */}
        <div className="oa-quick-actions">
          {QUICK_ACTIONS.map((qa) => (
            <button
              key={qa.label}
              className="oa-quick-btn"
              onClick={() => sendMessage(qa.query)}
            >
              {qa.label}
            </button>
          ))}
        </div>

        {/* Input */}
        <form className="oa-input-row" onSubmit={handleSubmit}>
          <input
            ref={inputRef}
            id="om-assist-input"
            className="oa-input"
            type="text"
            placeholder="Ask about SmartShrink…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            autoComplete="off"
          />
          <button
            type="submit"
            className="oa-send-btn"
            disabled={!input.trim()}
            aria-label="Send message"
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="22" y1="2" x2="11" y2="13" />
              <polygon points="22 2 15 22 11 13 2 9 22 2" />
            </svg>
          </button>
        </form>

        {/* Footer */}
        <p className="oa-footer">Built by <strong>Om Prakash Lenka</strong> · TCS</p>
      </div>
    </>
  );
}

export default memo(OmAssist);
